import type { GuardarDatosUsuariosRequest } from "@/shared/types/interfaces/ApiGestionPartes/Contactos.interface";
import { TELEFONOS } from "@/shared/utils/Regex.utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, IconButton, TextField } from "@mui/material";
import { RiAccountCircleLine, RiImageAddLine } from "@remixicon/react";
import { useRef, useEffect, useState, type FunctionComponent, type ReactNode } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { PhoneInput } from "../../PhoneInput";
import { useGuardarDatosUsuariosV2 } from "@/shared/hooks/useConfiguracion.hook";
import customAlert from "../../CustomAlert";
import { useGenerarEnlacePruebaVidaVU } from "@/modules/VU/hooks/useVU.hook";
import { TipoPruebaVidaEnum } from "@/shared/types/enums/TipoPruebaVida.enum";

async function arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = "";
    let bytes = new Uint8Array(buffer);
    let len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

async function fileToBase64(file: File) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = reject;
        reader.onload = async () => {
            let data = await arrayBufferToBase64(reader.result as ArrayBuffer);
            resolve(data); // Base64 limpia, sin encabezado
        };

        reader.readAsArrayBuffer(file);
    });
}

function normalizarTelefono(numero: string) {
    // Elimina todo lo que no sea número
    const digits = numero.replace(/\D/g, "");

    // Si no tiene 10 dígitos, no se puede formatear
    if (digits.length !== 10) return digits;

    // Formato XXX-XXX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

interface PersonalFormValues{
    telefono: string
    correo: string
    foto: string
}

export const PersonalFormSchema : yup.ObjectSchema<PersonalFormValues> = yup.object({
    telefono: yup
        .string()
        .matches(TELEFONOS, 'Número de teléfono inválido. Debe comenzar con 809, 829 o 849.')
        .required('El teléfono es obligatorio'),
    correo: yup
        .string()
        .email('El Correo Electrónico es inválido')
        .required('El Correo Electrónico es obligatorio'),
    foto: yup
        .string()
        .required('La foto es obligatoria'),
})

interface PersonalFormProps {
    closeForm: () => void
    datosUsuario: GuardarDatosUsuariosRequest
    onRegisterFooter?: (node: ReactNode) => void
}

const PersonalForm: FunctionComponent<PersonalFormProps> = ({
    closeForm,
    datosUsuario,
    onRegisterFooter,
}) => {
    
    const [previewImage, setPreviewImage] = useState<string>(datosUsuario.Foto);
    const [imgName, setImgName] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { t } = useTranslation();

    const {
        register, handleSubmit, formState: { errors }, setValue, setError, reset, control, watch
    } = useForm<PersonalFormValues>({
        resolver: yupResolver(PersonalFormSchema),
        defaultValues: {
            foto: datosUsuario.Foto,
            telefono: datosUsuario.Telefono,
            correo: datosUsuario.Correo,
        }
    });

    const handleSelectImage = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Generar preview
        const url = URL.createObjectURL(file);
        setPreviewImage(url);

        // Convertir a Base64 limpio
        const base64 = (await fileToBase64(file)) as string;
        /* console.log(file)
        console.log(base64) */

        // Guardar en el formulario
        setValue("foto", base64, { shouldValidate: true });
        setImgName(() => file.name)
    };

    const handleCloseForm = () => {
        closeForm()
        reset()
    }

    const {
        mutate: mutateGuardarDatosUsuarios,
        isPending: isPendingGuardarDatosUsuarios
    } = useGuardarDatosUsuariosV2();
    
    let isSameEmail = watch('correo') === datosUsuario.Correo;
    
    const {
        mutate: mutateGenerarEnlacePruebaVidaVU,
        isPending: isPendingGenerarEnlacePruebaVidaVU
    } = useGenerarEnlacePruebaVidaVU();

    const handleChangeCorreo = (nuevoCorreo: string) => {
        console.log('iniciar a cambiar correo', nuevoCorreo)
        mutateGenerarEnlacePruebaVidaVU({
            IdUsuario: datosUsuario.IdUsuario,
            Razon: 'Solicitud del usuario: Cambio de correo.',
            TipoPruebaVida: TipoPruebaVidaEnum.CAMBIO_CORREO,
            NuevoEmail: nuevoCorreo
        }, {
            onSuccess: (res) => {
                console.log(res)
                closeForm()
                reset()
                customAlert({
                    icon: 'success',
                    titulo: `¡Te hemos enviado un correo para completar la configuración!`,
                    mensaje: `${res.message || "Revisa tu correo electrónico y haz clic en el enlace para validar tu identidad."}`,
                    cerrarEsc: false,
                    cerrarClicFuera: false,
                    textoBotonAceptar: "Revisaré mi correo",
                    botonCancelar: false,
                });
            },
            onError: (err) => {
                customAlert({
                    icon: 'error',
                    titulo: err.message || `Ha ocurrido un error al enviar el enlace para cambiar tu correo electrónico.`,
                    tiempo: 8,
                    toast: true,
                });
            }
        });
    }

    let disableActionsIsPending : boolean = isPendingGuardarDatosUsuarios || isPendingGenerarEnlacePruebaVidaVU

    const onSubmit: SubmitHandler<PersonalFormValues> = (data) => {
        customAlert({
            icon: "question",
            titulo: "¿Estás seguro(a) de actualizar tus datos personales? ",
            mensaje: isSameEmail ? "" : "Estas a punto de realizar un cambio mayor de tu información confidencial.",
            botonCancelar: true,
            cerrarEsc: false,
            cerrarClicFuera: false,
            textoBotonAceptar: "Sí",
            colorBotonCancelar: '#d33',
            textoBotonCancelar: "No",
            callback: (res) =>  {
                if (res) {
                    mutateGuardarDatosUsuarios({
                        Celular: normalizarTelefono(data.telefono),
                        Correo: datosUsuario.Correo,
                        Foto: data.foto,
                        IdUsuario: datosUsuario.IdUsuario,
                        NombreFoto: imgName,
                        Telefono: normalizarTelefono(data.telefono)
                    }, {
                        onSuccess: (res) => {
                            console.log(res)
                            if(!isSameEmail){
                                handleChangeCorreo(data.correo)
                            } else {
                                closeForm()
                                reset()
                                customAlert({
                                    icon: 'success',
                                    titulo: `¡Tus datos personales se han actualizado correctamente!`,
                                    mensaje: "Si actualizaste tu foto de perfil, cierra tu sesión y vuelve a iniciarla para ver los cambios de inmediato.",
                                    cerrarEsc: false,
                                    cerrarClicFuera: false,
                                    textoBotonAceptar: "Entendido",
                                    botonCancelar: false,
                                });
                            }
                        },
                        onError: (err) => {
                            customAlert({
                                icon: 'error',
                                titulo: err.message || `Ha ocurrido un error al actualizar tus datos personales`,
                                tiempo: 8,
                                toast: true,
                            });
                        }
                    });
                }          
            }
        });
    }

    const handleCloseFormRef = useRef(handleCloseForm);
    handleCloseFormRef.current = handleCloseForm;
    const handleSubmitRef = useRef(handleSubmit);
    handleSubmitRef.current = handleSubmit;
    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;

    useEffect(() => {
        if (!onRegisterFooter) return;
        onRegisterFooter(
            <>
                <Button
                    variant="outlined"
                    color='error'
                    onClick={() => handleCloseFormRef.current()}
                    disabled={disableActionsIsPending}
                >
                    {t('configuracionPerfil.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleSubmitRef.current(onSubmitRef.current)()}
                    className="normal-case rounded-xl px-6 shadow-md"
                    loading={disableActionsIsPending}
                    loadingPosition="end"
                    disabled={disableActionsIsPending}
                >
                    {t('configuracionPerfil.saveChanges')}
                </Button>
            </>
        );
        return () => { onRegisterFooter(null); };
    }, [disableActionsIsPending, onRegisterFooter, t]);

    return (
        <>
        <div className="flex flex-col h-full w-full">
            <form>
                <div className="flex flex-col items-center">
                    <div className="relative">
                        {
                            !previewImage
                            ? <RiAccountCircleLine size={140} color="#0050DA" className="w-[140px] h-[140px] object-cover rounded-full border border-gray-200"/>
                            : <img src={previewImage} alt={`imagen de usuario ${''}`} className="w-[140px] h-[140px] object-cover rounded-full border border-gray-200" />
                        }
                        <IconButton 
                            aria-label="" 
                            color="primary" 
                            className="absolute left-[80px]" 
                            onClick={handleSelectImage}
                            disabled={disableActionsIsPending}
                        >
                            <RiImageAddLine />
                        </IconButton>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleImageChange}
                        />
                    </div>
                    {
                        errors.foto && (
                            <p className="text-[#d32f2f] text-[0.75rem] font-montserrat font-normal leading-[1.66] mt-[3px] mx-[14px] w-full text-center">
                                {errors.foto.message?.toString()}
                            </p>
                        )
                    }
                </div>
                <div className="flex gap-2 my-4 mt-8">
                    <Controller
                        name="telefono"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                {...field}
                                label='Teléfono'
                                required
                                error={!!errors.telefono}
                                helperText={errors.telefono?.message?.toString()}
                                disabled={disableActionsIsPending}
                            />
                        )}
                    />
                    <TextField 
                        fullWidth
                        label='Correo Electrónico'
                        placeholder='Ingrese su correo electrónico'
                        variant='outlined'
                        required
                        sx={{backgroundColor: '#FFFFFF'}}
                        {...register('correo')}
                        error={errors.correo?.message ? true : false}
                        helperText={
                            errors.correo?.message?.toString()
                        }
                        disabled={disableActionsIsPending}
                    />
                </div>
            </form>
        </div>
        </>
    );
}

export default PersonalForm;